// BroadbandDashboard.js — Nebraska BEAD Broadband Infrastructure
// Follows the same patterns as SurveyDashboard.js and Chapter dashboards
import React from "react";
import "../DashboardStyles.css";
import bead from "../data/panhandle-bead.json";
import { ProviderCostChart, PanhandleShareChart, PanhandleTechChart } from "./BeadCharts";

const C = {
  navy:    "#122326",
  slate:   "#213A3E",
  terra:   "#A84F30",
  teraBg:  "#BF6847",
  goldLt:  "#DBDDAC",
  cream:   "#F0EFEB",
  warm:    "#F7F6F3",
  sand:    "#E2E0DB",
  white:   "#FFFFFF",
  alert:   "#B94040",
  amber:   "#D97706",
  blue:    "#1D4ED8",
};

// ── Panhandle figures ─────────────────────────────────────────────────────────
// From src/data/panhandle-bead.json, generated by scripts/build-panhandle-bead.js
// (NBO Final Proposal Exhibits B/C/E joined to NBO GIS project areas and BSL
// points, clipped to the 11 PADD counties). Re-run the script to refresh.
const PH = bead.panhandle;
const SW = bead.statewide;
const PH_TECH = bead.byTechnology;
const fmtN = (v) => Math.round(v).toLocaleString();
const fmt$ = (v) => `$${Math.round(v).toLocaleString()}`;
const fmtM = (v, d = 1) => `$${(v / 1e6).toFixed(d)}M`;
const pct1 = (part, whole) => `${((part / whole) * 100).toFixed(1)}%`;
const pct0 = (part, whole) => `${Math.round((part / whole) * 100)}%`;
const PH_LOC_PCT = pct1(PH.locations, SW.locations);
const PH_BEAD_PCT = pct1(PH.bead_support, SW.bead_support);

// ── Stat card data ────────────────────────────────────────────────────────────
const STAT_CARDS = [
  { label: "NE BEAD Allocation",         value: "$405,000,000",   color: C.navy  },
  { label: "Deployed to Infrastructure", value: "$44,555,557",    color: C.blue  },
  { label: "Undeployed (At Risk)",        value: "~$360,000,000",  color: C.alert },
  { label: "Statewide Locations Covered",value: fmtN(SW.locations), color: C.slate, note: `plus ${SW.cais} community anchor institutions` },
  { label: "Panhandle Locations",         value: `${fmtN(PH.locations)} (${PH_LOC_PCT})`, color: C.terra, note: "Share of statewide BEAD locations" },
  { label: "Panhandle BEAD Funding",      value: fmt$(PH.bead_support), color: C.terra, note: `${PH_BEAD_PCT} of statewide BEAD support` },
  // Mobius Communications: CM61-BEAD-NE-D704-MOB (260 BSLs) + E601-MOB (33 BSLs), Dawes County (Exhibit B)
  { label: "Panhandle Fiber Projects",    value: String(PH_TECH.Fiber.projects), color: C.blue,
    note: `Mobius — Dawes Co., ${fmtN(PH_TECH.Fiber.locations)} locations, ${fmtM(PH_TECH.Fiber.bead_support, 2)}` },
  { label: "Panhandle CAIs Funded",       value: String(PH.cais),  color: C.alert },
  // Starlink + Kuiper combined (Exhibit B/C)
  { label: "Avg Cost/Location (LEO Satellite)", value: "$1,001",  color: C.amber, note: "Starlink $759 · Kuiper $1,529" },
  { label: "Avg Cost/Location (Fiber)",   value: "$15,568",       color: C.blue  },
  { label: "Eligible Locations Lost (statewide)", value: "~15,400 (52%)", color: C.alert },
];

// ── Chart metadata ────────────────────────────────────────────────────────────
const CHARTS = [
  {
    file: "chart1_tech_mix.png",
    title: "Technology Allocation: Locations vs. Funding",
    caption: "Fixed wireless serves 57% of locations but receives 44% of BEAD funding. Fiber serves only 9% of locations but absorbs 45% of funding. Source: NBO Final Proposal, Exhibit B.",
  },
  {
    key: "cost-per-location",
    render: () => <ProviderCostChart providers={PROVIDERS} />,
    title: "Cost Per Location by Provider & Technology",
    caption: "Average BEAD award per location. SpaceX Starlink averages $759/location vs. $23,192 for Midstates Data Transport (Stealth) fiber. CAI-only projects (Quick Current, Northeast NE Telephone) are excluded. Source: NBO Final Proposal, Exhibit B.",
  },
  {
    file: "chart3_funding_gap.png",
    title: "Nebraska's $360M BEAD Funding Gap",
    caption: "Only 11% of Nebraska's $405M BEAD allocation is being deployed. Governor Pillen has advocated retaining undeployed funds for precision agriculture, biotech, and AI. Status of remaining funds expected in early 2026.",
  },
  {
    key: "panhandle-share",
    render: () => <PanhandleShareChart />,
    title: "Panhandle's Share of Nebraska BEAD",
    caption: `The Panhandle has ${fmtN(PH.locations)} of Nebraska’s ${fmtN(SW.locations)} BEAD locations (${PH_LOC_PCT}) and ${fmtM(PH.bead_support)} of ${fmtM(SW.bead_support)} in BEAD support (${PH_BEAD_PCT}). Source: NBO Final Proposal Exhibits B–C; locations placed by county using NBO’s BEAD project-area GIS.`,
  },
  {
    key: "panhandle-tech",
    render: () => <PanhandleTechChart />,
    title: "Panhandle Technology Mix vs. Statewide",
    caption: `Share of BEAD locations by technology. LEO satellite is ${pct0(PH_TECH["LEO Satellite"].locations, PH.locations)} of Panhandle locations vs. ${pct0(SW.locationsByTechnology["LEO Satellite"], SW.locations)} statewide; fiber is ${pct0(PH_TECH.Fiber.locations, PH.locations)} (Mobius, Dawes County). The region has zero funded Community Anchor Institutions. Source: NBO Final Proposal Exhibits B, C and E.`,
  },
  {
    file: "chart6_timeline.png",
    title: "Deployment Timeline by Provider",
    caption: "Satellite providers target completion by early 2027. Fixed wireless and fiber builds extend through late 2029. As of April 2026, zero locations have been marked 'served.'",
  },
  {
    file: "chart7_eligibility_loss.png",
    title: "Shrinking Eligibility: 52% Location Reduction",
    caption: "Nebraska's eligible locations dropped from ~29,500 to 14,032 (14,064 including the 32 anchor institutions, as plotted) after FCC map updates and reclassification of unlicensed wireless coverage. Most other states had minimal reductions.",
  },
];

// ── Implications ──────────────────────────────────────────────────────────────
const IMPLICATIONS = [
  {
    head: "Limited Fiber in the Panhandle",
    body: `The only BEAD fiber awards in the 11-county region are Mobius Communications’ two projects in rural Dawes County (${fmtN(PH_TECH.Fiber.locations)} locations, ${fmtM(PH_TECH.Fiber.bead_support, 2)}, including Chadron State Park). The other Panhandle awards are LEO satellite (${fmtN(PH_TECH["LEO Satellite"].locations)} locations) and licensed fixed wireless (${fmtN(PH_TECH["Licensed Fixed Wireless"].locations)}). Fiber infrastructure — critical for precision agriculture, telehealth, remote work, and AI — remains scarce in the federal deployment plan.`,
  },
  {
    head: "Zero Community Anchor Institutions",
    // TODO: "700+" eligible CAIs has no citation in the NBO exhibits; Exhibit E lists 32 funded CAIs.
    body: "Of 700+ [needs verification] statewide CAIs identified as eligible, NBO funded only 32 — none in the Panhandle. Libraries, schools, health centers, and fire departments in the region will not receive BEAD-funded gigabit fiber connections.",
  },
  {
    head: "Satellite Performance Risk",
    body: "Ookla speed test data from late 2025 showed only ~45% of Starlink subscribers met BEAD's minimum 100/20 Mbps threshold. SpaceX has requested modified performance testing rules, drawing scrutiny from Congress.",
  },
  {
    head: "$360M at Risk",
    body: "The undeployed BEAD funds represent a once-in-a-generation opportunity. PADD should advocate for non-deployment fund allocation to Panhandle priorities: digital literacy, telehealth infrastructure, precision ag connectivity, and workforce development.",
  },
  {
    head: "Complementary Funding Needed",
    body: "The CEDS should identify supplemental broadband investment sources: Nebraska Broadband Bridge Program ($20M/year), USDA ReConnect, Digital Equity Capacity Grant ($6.5M to NBO), and state Universal Service Fund support.",
  },
];

// ── Provider table data ───────────────────────────────────────────────────────
// signed: subgrant agreement signed, per NBO press release, May 22, 2026.
// locs: Exhibit C BSLs plus Exhibit E CAIs; funding: Exhibit B BEAD support.
const PROVIDERS = [
  { name: "AMG/Nextlink",          short: "AMG/Nextlink",        tech: "Licensed Fixed Wireless", locs: 7887, funding: 19070540, signed: true  },
  { name: "SpaceX Starlink",       short: "SpaceX Starlink",     tech: "LEO Satellite",           locs: 3268, funding: 2480672,  signed: true  },
  { name: "Amazon Kuiper",         short: "Amazon Kuiper",       tech: "LEO Satellite",           locs: 1496, funding: 2287450,  signed: false },
  // CM61-BEAD-NE-ALL-MDT, "413 BSLs and 10 CAIs" — Midstates Data Transport LLC dba Stealth
  { name: "Midstates Data Transport (Stealth)", short: "Midstates (Stealth)", tech: "Fiber",  locs: 423,  funding: 9810300,  signed: true  },
  { name: "Nedelco",               short: "Nedelco",             tech: "Fiber",                   locs: 328,  funding: 5155738,  signed: true  },
  { name: "Mobius Communications", short: "Mobius Comm.",        tech: "Fiber",                   locs: 293,  funding: 1979312,  signed: true  },
  { name: "Pinpoint Communications", short: "Pinpoint Comm.",    tech: "Fiber",                   locs: 232,  funding: 2601566,  signed: false },
  { name: "Vistabeam (Inventive Wireless of NE)*", short: "Vistabeam", tech: "Licensed Fixed Wireless", locs: 115, funding: 510000, signed: true },
  { name: "Quick Current-NE",      short: "Quick Current-NE",    tech: "Fiber/CAI",               locs: 17,   funding: 509983,   signed: true  },
  { name: "NE NE Telephone",       short: "NE NE Telephone",     tech: "Fiber/CAI",               locs: 5,    funding: 149995,   signed: false },
];

// ── Tech badge color ──────────────────────────────────────────────────────────
function techColor(tech) {
  if (tech.includes("Fiber"))    return { bg: "#EEF2FF", color: C.blue };
  if (tech.includes("Satellite")) return { bg: "#FEF3C7", color: C.amber };
  return { bg: C.cream, color: C.slate };
}

// ── Section wrapper (matches SurveyDashboard Section pattern) ─────────────────
function Section({ title, subtitle, children }) {
  return (
    <div style={{ marginBottom: 40 }}>
      <h3 style={{ color: C.navy, marginBottom: 2, fontSize: "1.05rem" }}>{title}</h3>
      {subtitle && (
        <p style={{ color: "#666", fontSize: 12, margin: "0 0 16px" }}>{subtitle}</p>
      )}
      {children}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function BroadbandDashboard() {
  return (
    <div className="chapter">
      <div className="chapter-content">

        {/* ── Title ── */}
        <h2>Nebraska BEAD Broadband Infrastructure</h2>

        <div className="callout">
          <span className="callout-label">Status update — June 2026</span>
          <p>
            The analysis below reflects the NBO Final Proposal (December 2025).
            Since then, seven ISPs signed subgrant agreements covering
            approximately 88 percent of Nebraska’s 14,032 BEAD-eligible
            locations; three providers declined to finalize their awards,
            leaving 1,735 locations without a committed provider, and Nebraska
            has reopened its BEAD bidding portal. See Chapter 4.3 for the current
            status and Objective 3.2 for how the CEDS tracks broadband progress.
          </p>
          <p style={{ marginTop: 10, fontSize: 13 }}>
            <em>
              This page reflects data current as of September 2026. The adopted
              2026–2031 CEDS, submitted September 2026, remains the document of
              record. Where this page shows newer figures, the CEDS will be updated
              at the next annual review.
            </em>
          </p>
        </div>

        {/* ── Section 1: Overview card (navy header, matches SurveyDashboard style) ── */}
        <div style={{
          background: C.navy, color: C.white, borderRadius: 10,
          padding: "24px 28px", marginBottom: 32,
        }}>
          <div style={{
            fontSize: 11, fontWeight: 700, textTransform: "uppercase",
            letterSpacing: "0.07em", color: C.goldLt, marginBottom: 8,
          }}>
            Nebraska BEAD Program: Panhandle Impact
          </div>
          <p style={{ color: "#D0D8DA", fontSize: 14, lineHeight: 1.7, margin: "0 0 14px" }}>
            Nebraska was allocated <strong style={{ color: C.goldLt }}>$405 million</strong> through
            the federal Broadband Equity, Access, and Deployment (BEAD) program in 2023. Following
            NTIA's June 2025 Revised Policy Notice and the state's approved Final Proposal (December 2025),
            only <strong style={{ color: C.goldLt }}>$44.6 million (11%)</strong> will be deployed to
            connect {fmtN(SW.locations)} remaining unserved locations and {SW.cais} community anchor institutions. The remaining ~$360 million is
            undeployed and at risk of returning to the federal government.
          </p>
          <p style={{ color: "#D0D8DA", fontSize: 14, lineHeight: 1.7, margin: 0 }}>
            The Panhandle (PADD's 11-county region) contains{" "}
            <strong style={{ color: C.goldLt }}>{fmtN(PH.locations)} BEAD award locations — {PH_LOC_PCT} of the statewide total</strong>{" "}
            — and receives <strong style={{ color: C.goldLt }}>{fmtM(PH.bead_support)} in BEAD support ({PH_BEAD_PCT})</strong>,
            with {fmtM(PH.match)} in provider match. The region has{" "}
            <strong style={{ color: "#FCA5A5" }}>zero funded Community Anchor Institutions</strong> under
            BEAD. By location, Panhandle awards are {pct0(PH_TECH["LEO Satellite"].locations, PH.locations)} LEO
            satellite, {pct0(PH_TECH.Fiber.locations, PH.locations)} fiber (Mobius Communications’ two projects in
            Dawes County), and {pct0(PH_TECH["Licensed Fixed Wireless"].locations, PH.locations)} licensed fixed wireless.
          </p>
        </div>

        {/* ── Section 2: Key Statistics Grid ── */}
        <Section
          title="Key Statistics"
          subtitle="Nebraska BEAD program data — NBO Final Proposal, December 2025"
        >
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 14,
            marginBottom: 8,
          }}>
            {STAT_CARDS.map((s) => (
              <div key={s.label} style={{
                background: C.white, border: `1px solid ${C.sand}`,
                borderTop: `4px solid ${s.color}`, borderRadius: 8,
                padding: "16px 18px",
              }}>
                <div style={{
                  fontSize: "1.5rem", fontWeight: 800, color: s.color,
                  lineHeight: 1, marginBottom: 6,
                }}>
                  {s.value}
                </div>
                <div style={{ fontSize: 12, color: C.slate, lineHeight: 1.4 }}>{s.label}</div>
                {s.note && (
                  <div style={{ fontSize: 11, color: "#767676", lineHeight: 1.4, marginTop: 4 }}>{s.note}</div>
                )}
              </div>
            ))}
          </div>
        </Section>

        {/* ── Section 3: Charts ── */}
        <Section
          title="Data Visualizations"
          subtitle="Source: NBO Final Proposal (Exhibits B, C, E), NBO BEAD project-area GIS, FCC BDC Map, Ookla speed test data"
        >
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
            gap: 24,
          }}>
            {CHARTS.map((chart) => (
              <div key={chart.key || chart.file} style={{
                background: C.white, border: `1px solid ${C.sand}`,
                borderRadius: 8, overflow: "hidden",
              }}>
                {chart.render ? (
                  <div style={{ padding: "14px 10px 0" }} role="img" aria-label={chart.title}>
                    {chart.render()}
                  </div>
                ) : (
                  <img
                    src={process.env.PUBLIC_URL + "/images/" + chart.file}
                    alt={chart.title}
                    style={{ width: "100%", display: "block" }}
                  />
                )}
                <div style={{ padding: "14px 16px" }}>
                  <div style={{
                    fontWeight: 700, fontSize: 13, color: C.navy, marginBottom: 6,
                  }}>
                    {chart.title}
                  </div>
                  <p style={{
                    fontSize: 11, color: "#767676", lineHeight: 1.55, margin: 0,
                  }}>
                    {chart.caption}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* ── Section 4: CEDS Implications ── */}
        <Section title="Implications for Panhandle Economic Development">
          <div style={{
            background: C.cream, borderLeft: `4px solid ${C.terra}`,
            borderRadius: "0 8px 8px 0", padding: "20px 24px",
          }}>
            {IMPLICATIONS.map((item, i) => (
              <div key={i} style={{ marginBottom: i < IMPLICATIONS.length - 1 ? 18 : 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: C.navy, marginBottom: 4 }}>
                  {item.head}
                </div>
                <p style={{ fontSize: 13, color: C.slate, lineHeight: 1.65, margin: 0 }}>
                  {item.body}
                </p>
              </div>
            ))}
          </div>
        </Section>

        {/* ── Section 5: Data Sources ── */}
        <Section title="Data Sources & References">
          <div style={{
            background: C.white, border: `1px solid ${C.sand}`,
            borderRadius: 8, padding: "18px 22px", marginBottom: 20,
          }}>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: C.slate, lineHeight: 1.8 }}>
              <li>
                Nebraska Broadband Office Final Proposal (NTIA Approved, Dec. 2025):{" "}
                <a href="https://broadband.nebraska.gov/nbead/final-proposal/" target="_blank" rel="noreferrer"
                  style={{ color: C.terra }}>
                  broadband.nebraska.gov/nbead/final-proposal/
                </a>
              </li>
              <li>NBO Exhibit B — Deployment Projects CSV</li>
              <li>NBO Exhibit A — Subgrantees CSV</li>
              <li>NBO Exhibit E — Community Anchor Institutions CSV</li>
              <li>
                NBO BEAD Defined Project Areas and project-area BSL points (Nebraska DOT GIS, gis.ne.gov), used to place
                awarded locations in the 11 Panhandle counties
              </li>
              <li>
                Cross-checked against FCC Broadband Data Collection location data (census blocks): every Exhibit C
                location matched to its county; Panhandle totals agree project by project
              </li>
              <li>
                Nebraska Broadband Map Dashboard:{" "}
                <a href="https://experience.arcgis.com/experience/f1f3ece6ff1a46cdb8c92a7b29e2574e/" target="_blank" rel="noreferrer"
                  style={{ color: C.terra }}>
                  ArcGIS Experience
                </a>
              </li>
              <li>FCC BDC Map — Nebraska (v2), data current to Dec. 31, 2022</li>
              <li>NBO Annual Report Testimony to Transportation &amp; Telecommunications Committee, Dec. 4, 2024</li>
              <li>Governor Pillen Press Release, Dec. 3, 2025</li>
              <li>
                Nebraska Broadband Office press release on signed subgrant agreements, May 22, 2026:{" "}
                <a href="https://dot.nebraska.gov/media/xvllowbe/20260522-nbo-subgrant-agreements.pdf" target="_blank" rel="noreferrer"
                  style={{ color: C.terra }}>
                  dot.nebraska.gov
                </a>
              </li>
              <li>
                Alliance Times-Herald, “Mobius Communications Company advances broadband expansion in Dawes County with BEAD grant support”:{" "}
                <a href="https://alliancetimes.com/mobius-communications-company-advances-broadband-expansion-in-dawes-county-with-bead-grant-support/" target="_blank" rel="noreferrer"
                  style={{ color: C.terra }}>
                  alliancetimes.com
                </a>
              </li>
            </ul>
          </div>

          {/* Provider table */}
          <h3 style={{ fontSize: "1rem", marginBottom: 10 }}>Statewide Provider Data</h3>
          <div style={{ overflowX: "auto" }}>
            <table style={{
              width: "100%", borderCollapse: "collapse", fontSize: 12,
              background: C.white, border: `1px solid ${C.sand}`, borderRadius: 8,
              overflow: "hidden",
            }}>
              <thead>
                <tr style={{ background: C.cream }}>
                  {["Provider", "Technology", "Locations", "BEAD Funding", "Avg $/Location", "Status (June 2026)"].map((h) => (
                    <th key={h} style={{
                      padding: "9px 12px", textAlign: "left",
                      fontWeight: 700, fontSize: 11, color: C.slate,
                      letterSpacing: "0.03em", borderBottom: `1px solid ${C.sand}`,
                      whiteSpace: "nowrap",
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PROVIDERS.map((p, i) => {
                  const tc = techColor(p.tech);
                  return (
                    <tr key={p.name} style={{ background: i % 2 === 0 ? C.white : C.warm }}>
                      <td style={{ padding: "8px 12px", color: C.navy, fontWeight: 600, borderBottom: `1px solid ${C.sand}` }}>
                        {p.name}
                      </td>
                      <td style={{ padding: "8px 12px", borderBottom: `1px solid ${C.sand}` }}>
                        <span style={{
                          display: "inline-block", padding: "2px 8px", borderRadius: 4,
                          fontSize: 11, fontWeight: 600,
                          background: tc.bg, color: tc.color,
                        }}>
                          {p.tech}
                        </span>
                      </td>
                      <td style={{ padding: "8px 12px", color: C.slate, borderBottom: `1px solid ${C.sand}`, textAlign: "right" }}>
                        {fmtN(p.locs)}
                      </td>
                      <td style={{ padding: "8px 12px", color: C.slate, borderBottom: `1px solid ${C.sand}`, textAlign: "right" }}>
                        {fmt$(p.funding)}
                      </td>
                      <td style={{ padding: "8px 12px", color: C.slate, borderBottom: `1px solid ${C.sand}`, textAlign: "right" }}>
                        {fmt$(p.funding / p.locs)}
                      </td>
                      <td style={{
                        padding: "8px 12px", borderBottom: `1px solid ${C.sand}`,
                        color: p.signed ? C.slate : C.alert, fontWeight: p.signed ? 400 : 600, whiteSpace: "nowrap",
                      }}>
                        {p.signed ? "Signed" : "Not signed"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: 11, color: "#999", marginTop: 8 }}>
            Source: NBO Final Proposal Exhibits B, C and E. Locations and funding figures as of December 2025;
            locations include community anchor institutions (Midstates 10, Quick Current 17, NE NE Telephone 5).
            Status: signed subgrant agreements per NBO press release, May 22, 2026.
          </p>
          <p style={{ fontSize: 11, color: "#999", marginTop: 4 }}>
            * Light Reading (2026) reports Vistabeam’s final NE award as 93 locations / $432,375;
            the table reflects the Dec 2025 Final Proposal.
          </p>
        </Section>

      </div>
    </div>
  );
}
